<script setup>
import { ref, onMounted } from 'vue'
import { useToast } from 'vue-toastification'
import { useDashboardStore } from '@/stores/dashboard'

const props = defineProps({
  dashboard: {
    type: Object,
    required: true
  },
  onDelete: {
    type: Function,
    required: true
  }
})

const emit = defineEmits(['modal-close'])
const toast = useToast()
const dashboardStore = useDashboardStore()

const deleting = ref(false)
const loadingSavedSearches = ref(true)
const savedSearches = ref([])
const selectedIds = ref(new Set())

onMounted(async () => {
  try {
    const searches = await dashboardStore.getDashboardSavedSearches(props.dashboard.id)
    savedSearches.value = searches
    // Default: all checked (delete)
    selectedIds.value = new Set(searches.map(s => s.id))
  } catch (e) {
    // Non-fatal — just show no saved searches
  } finally {
    loadingSavedSearches.value = false
  }
})

function toggleSearch(id) {
  const next = new Set(selectedIds.value)
  if (next.has(id)) {
    next.delete(id)
  } else {
    next.add(id)
  }
  selectedIds.value = next
}

function selectAll() {
  selectedIds.value = new Set(savedSearches.value.map(s => s.id))
}

function selectNone() {
  selectedIds.value = new Set()
}

async function doDelete() {
  if (deleting.value) return
  deleting.value = true
  try {
    await props.onDelete(Array.from(selectedIds.value))
    emit('modal-close')
  } catch (e) {
    toast.error(e.message || 'Failed to delete dashboard')
  } finally {
    deleting.value = false
  }
}

function truncateQuery(query, maxLength = 50) {
  if (!query) return ''
  return query.length > maxLength ? query.substring(0, maxLength) + '...' : query
}
</script>

<template>
  <div class="delete-modal">
    <h3 class="modal-title">Delete Dashboard</h3>
    <p class="modal-description">
      Are you sure you want to delete <strong>{{ dashboard.name }}</strong>? This cannot be undone.
    </p>

    <!-- Loading saved searches -->
    <div v-if="loadingSavedSearches" class="loading-searches">
      <font-awesome-icon :icon="['fas', 'spinner']" spin />
      <span>Checking saved searches...</span>
    </div>

    <!-- Saved searches list -->
    <div v-else-if="savedSearches.length > 0" class="saved-searches-section">
      <div class="section-header">
        <span class="section-label">Also delete these saved searches?</span>
        <div class="select-actions">
          <button class="btn-select" @click="selectAll">All</button>
          <button class="btn-select" @click="selectNone">None</button>
        </div>
      </div>
      <div class="searches-list">
        <label
          v-for="ss in savedSearches"
          :key="ss.id"
          class="search-row"
          @click.prevent="toggleSearch(ss.id)"
        >
          <span class="checkbox" :class="{ 'is-checked': selectedIds.has(ss.id) }">
            <font-awesome-icon v-if="selectedIds.has(ss.id)" :icon="['fas', 'check']" />
          </span>
          <span class="search-info">
            <span class="search-name">{{ ss.name }}</span>
            <span class="search-query">{{ truncateQuery(ss.query) }}</span>
          </span>
        </label>
      </div>
    </div>

    <div class="modal-actions">
      <button
        class="btn-delete"
        :disabled="deleting"
        @click="doDelete"
      >
        <font-awesome-icon v-if="deleting" :icon="['fas', 'spinner']" spin />
        <font-awesome-icon v-else :icon="['fas', 'trash']" />
        {{ deleting ? 'Deleting...' : 'Delete' }}
      </button>
      <button class="btn-close-modal" @click="emit('modal-close')">
        Cancel
      </button>
    </div>
  </div>
</template>

<style scoped>
.delete-modal {
  width: 500px;
  max-width: 90vw;
}

.modal-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: #f3f4f6;
  margin: 0 0 0.5rem 0;
}

.modal-description {
  font-size: 0.8125rem;
  color: #9ca3af;
  margin: 0 0 1rem 0;
}

.modal-description strong {
  color: #e5e7eb;
}

.loading-searches {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem;
  color: #6b7280;
  font-size: 0.8125rem;
}

.saved-searches-section {
  margin-bottom: 0.75rem;
}

.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.5rem;
}

.section-label {
  font-size: 0.75rem;
  color: #6b7280;
  font-weight: 500;
}

.select-actions {
  display: flex;
  gap: 0.375rem;
}

.btn-select {
  padding: 0.125rem 0.5rem;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 0.25rem;
  color: #6b7280;
  font-size: 0.6875rem;
  cursor: pointer;
  transition: all 0.15s ease;
}

.btn-select:hover {
  background: rgba(255, 255, 255, 0.1);
  color: #9ca3af;
}

.searches-list {
  max-height: 200px;
  overflow-y: auto;
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 0.5rem;
}

.searches-list::-webkit-scrollbar {
  width: 4px;
}

.searches-list::-webkit-scrollbar-track {
  background: transparent;
}

.searches-list::-webkit-scrollbar-thumb {
  background: #3E4A56;
  border-radius: 2px;
}

.search-row {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  padding: 0.5rem 0.75rem;
  cursor: pointer;
  transition: background 0.15s ease;
  user-select: none;
}

.search-row:hover {
  background: rgba(255, 255, 255, 0.03);
}

.search-row + .search-row {
  border-top: 1px solid rgba(255, 255, 255, 0.04);
}

.checkbox {
  width: 1.125rem;
  height: 1.125rem;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 0.25rem;
  color: transparent;
  font-size: 0.625rem;
  transition: all 0.15s ease;
  flex-shrink: 0;
}

.checkbox.is-checked {
  background: rgba(239, 68, 68, 0.2);
  border-color: rgba(239, 68, 68, 0.4);
  color: #ef4444;
}

.search-info {
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
  min-width: 0;
}

.search-name {
  font-size: 0.8125rem;
  color: #e5e7eb;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.search-query {
  font-size: 0.6875rem;
  color: #6b7280;
  font-family: monospace;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  margin-top: 1rem;
}

.btn-delete {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: rgba(239, 68, 68, 0.15);
  border: 1px solid rgba(239, 68, 68, 0.3);
  border-radius: 0.375rem;
  color: #ef4444;
  font-size: 0.8125rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-delete:hover:not(:disabled) {
  background: rgba(239, 68, 68, 0.25);
  border-color: rgba(239, 68, 68, 0.5);
}

.btn-delete:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-close-modal {
  padding: 0.5rem 1rem;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 0.375rem;
  color: #9ca3af;
  font-size: 0.8125rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-close-modal:hover {
  background: rgba(255, 255, 255, 0.1);
  color: #d1d5db;
}
</style>
